import { makeAutoObservable } from "mobx";

/*
  An outbox of events the server hasn't confirmed yet — NOT session/UI state,
  which is why it lives here rather than on sessionStore (the one store that
  deliberately persists nothing).

  Logging an event goes through Kinesis before it lands in DynamoDB, and in the
  meantime getNextSteps / getEarnedBadges need to know about it. Both resolvers
  accept the events we hold here and merge them with what's already persisted,
  deduplicating on `data.uuid`.

  Persisted per user, because the buffer used to die with the tab: a badge
  earned immediately before a reload was never awarded at all
  (useNewBadgeCheck bails when there are no events to evaluate, and the
  react-query cache then re-serves the pre-badge list).
*/

export type RecentEvent = {
  id: string;
  eventType: string;
  data: string;
  timestamp: string;
};

/**
 * These events only exist to cover the lag between logging and DynamoDB. Past
 * that window the server already has them, so keeping them would inflate every
 * badge/next-step payload for no benefit.
 */
const MAX_AGE_MS = 10 * 60 * 1000;

/** Backstop against unbounded growth; age is the real eviction rule. */
const MAX_EVENTS = 100;

const STORAGE_VERSION = "V1";

class EventOutbox {
  events: RecentEvent[] = [];

  /** The user whose events are currently loaded, so a switch reloads them. */
  private loadedFor: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  add(event: RecentEvent) {
    if (!event) return;

    // A uuid inside `data` is what the resolvers deduplicate on; without one
    // the event is silently ignored server-side, so it would sit here as dead
    // weight until it aged out.
    if (!this.hasUuid(event)) {
      console.warn(
        "Recent event has no data.uuid and will be ignored by the server",
        event.eventType,
      );
    }

    // Replace rather than push: a mutated array doesn't reliably re-render
    // consumers that aren't MobX observers, and the badge check keys off the
    // length changing.
    this.events = this.withinMaxAge(this.currentEvents()).concat(event);

    if (this.events.length > MAX_EVENTS) {
      const dropped = this.events.length - MAX_EVENTS;
      console.warn(
        `Recent event buffer over ${MAX_EVENTS}; dropping ${dropped} oldest`,
      );
      this.events = this.events.slice(-MAX_EVENTS);
    }

    this.save();
  }

  /** Every event still worth sending to the server. */
  all(): RecentEvent[] {
    const fresh = this.withinMaxAge(this.currentEvents());
    // Pruning on read keeps a long-idle tab from resending stale events.
    if (fresh.length !== this.events.length) {
      this.events = fresh;
      this.save();
    }
    return [...fresh];
  }

  /** Call on logout: a shared device must not carry events between users. */
  clear() {
    const userId = this.userId();
    this.events = [];
    this.loadedFor = null;
    if (userId) {
      try {
        localStorage.removeItem(this.storageKey(userId));
      } catch (e) {
        console.warn("Failed to clear recent events", e);
      }
    }
  }

  // -------- Persistence helpers --------

  private storageKey(userId: string) {
    return `RECENT_EVENTS_${STORAGE_VERSION}_${userId}`;
  }

  private userId(): string | null {
    if (typeof window === "undefined") return null;
    try {
      return localStorage.getItem("currentUserId");
    } catch {
      return null;
    }
  }

  private hasUuid(event: RecentEvent): boolean {
    try {
      return Boolean(JSON.parse(event.data)?.uuid);
    } catch {
      return false;
    }
  }

  private withinMaxAge(events: RecentEvent[]): RecentEvent[] {
    const cutoff = Date.now() - MAX_AGE_MS;
    return events.filter((event) => {
      const at = Date.parse(event.timestamp);
      // Keep anything we can't date rather than silently discarding it.
      return !Number.isFinite(at) || at >= cutoff;
    });
  }

  /**
   * The in-memory list, hydrating from storage the first time this user's
   * events are needed. Hydration is lazy because the user id isn't known at
   * construction time.
   */
  private currentEvents(): RecentEvent[] {
    const userId = this.userId();
    if (userId && this.loadedFor !== userId) {
      this.events = this.load(userId);
      this.loadedFor = userId;
    }
    return this.events;
  }

  private load(userId: string): RecentEvent[] {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem(this.storageKey(userId));
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return this.withinMaxAge(parsed as RecentEvent[]);
    } catch (e) {
      console.warn("Failed to load recent events from storage", e);
      return [];
    }
  }

  private save() {
    const userId = this.userId();
    if (!userId) return;
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(
        this.storageKey(userId),
        JSON.stringify(this.events),
      );
    } catch (e) {
      console.warn("Failed to persist recent events", e);
    }
  }
}

const eventOutbox = new EventOutbox();
export default eventOutbox;
